// moms-harness:anchor v2
// Spec: docs/spec/settlement/SPEC-settlement-mwp-mlt-sku-attribute-registration-A.yaml
// Spec: docs/spec/settlement/SPEC-settlement-mwp-mlt-sku-attribute-registration-C.yaml
// JIRA: LOGISPRD-13286, LOGISPRD-13480, LOGISPRD-13852, LOGISPRD-14571
import type { FilterFieldSpec, PboTableColumn } from '@musinsa/pbo-core'
import { SHIPPER_REQUIRED_MARK_ONLY } from '@/components/system/shipperSelectFilter'
// 폭·라벨 표의 키를 행 타입에 묶어 오타를 컴파일로 잡는다 (리뷰 지적 — Record<string, …> 는
// 키가 틀려도 undefined 를 읽어 검증이 조용히 통과한다).
//
// **queries/types.ts 에서 가져온다** — useGetSkuAttributeList 는 이 파일을 import 하므로
// 그쪽에서 가져오면 사이클이 된다 (PR #810 3차 봇 리뷰). types.ts 는 leaf 다
import type { SkuAttributeRow } from '../queries/types'

/**
 * MA18 이 다루는 화주유형은 **MWP·MLT 둘뿐이다.**
 *
 * MA02 처럼 `SHIPPER_TYPES` 전체를 쓰지 않는다 — 등록이 MWP·MLT 전용이라 다른 유형을 고르면
 * BE 가 빈 목록을 돌려주고 사용자는 「왜 안 나오지」가 된다. 고를 수 없게 하는 편이 낫다.
 */
export const SERVICE_TYPE_OPTIONS = [
  { label: 'MWP', value: 'MWP' },
  { label: 'MLT', value: 'MLT' },
]

export const FILTER_FIELDS: FilterFieldSpec[] = [
  {
    id: 'f-shipper',
    type: 'logistics.ShipperSelect',
    label: '화주유형/화주명',
    // 별표만 남기고 칸 단위 필수 검증은 끈다 — MA02 와 같은 이유(`SHIPPER_REQUIRED_MARK_ONLY`).
    // 화주 축이 있는지는 `hasShipperFilter` 가 판정한다
    ...SHIPPER_REQUIRED_MARK_ONLY,
    typeName: 'logisticsServiceTypes',
    nameName: 'shipperNames',
    typeOptions: SERVICE_TYPE_OPTIONS,
    nameOptions: [], // 실 화주 옵션 주입 (SkuAttributeFilter)
  },
  // LOGISPRD-13852 — 이 칸만 **다건**이다. pbo-core FormInput 의 다건 입력(우측 + 버튼)으로 여러 건을
  // 붙여 넣으면 `skuIds` 로 그대로 보낸다. BE 가 정확일치(IN)로 받는다 — 식별자라 검색어가 아니다
  { id: 'f-skuId', type: 'mfs.FormInput', label: 'SKU ID', filterKey: 'sku_id' },
  { id: 'f-barcode', type: 'mfs.FormInput', label: '바코드', filterKey: 'barcode' },
  { id: 'f-itemCode', type: 'mfs.FormInput', label: '상품코드', filterKey: 'item_code' },
  { id: 'f-itemName', type: 'mfs.FormInput', label: '상품명', filterKey: 'item_name' },
  { id: 'f-brandName', type: 'mfs.FormInput', label: '브랜드명', filterKey: 'brand_name' },
  // LOGISPRD-13480 — 두 축 모두 BE 가 부분일치로 받는다.
  // **상품단위는 필터에서 뺐다** (LOGISPRD-13852 업무 요청). 표의 상품단위 열·셀 입력은 그대로다
  { id: 'f-color', type: 'mfs.FormInput', label: '색상', filterKey: 'color' },
  { id: 'f-styleNumber', type: 'mfs.FormInput', label: '스타일번호', filterKey: 'style_number' },
]

/**
 * SKU ID 다건 조회 1회의 상한 — **BE 와 같은 값이어야 한다**
 * (`SkuAttributeManagementController.MAX_SKU_ID_FILTER_SIZE`).
 *
 * 100 인 이유는 GET 쿼리스트링 크기다 — sku_id 한 건이 약 15~22바이트라 1,000건이면 서버의 요청 헤더
 * 한도(8KB)를 넘어 상한에 닿기 전에 거절된다 (BE LLD §15.2).
 *
 * 화면에서 먼저 막는 것은 **안내 문구 때문이다.** BE 도 넘으면 400 이지만 그 응답은
 * 「유효하지 않은 요청입니다」 고정 문구만 나가 사용자가 무엇을 줄여야 하는지 모른다.
 */
export const MAX_SKU_ID_FILTER_SIZE = 100

/** SKU ID 한 건의 최대 길이 — BE `MAX_SKU_ID_LENGTH`(컬럼 폭)와 같다. 넘는 값은 어떤 SKU 와도 일치할 수 없다. */
export const MAX_SKU_ID_LENGTH = 70

/** [양식 다운로드] 파일명. 서버 다운로드(`상품기준정보_...`)와 이름을 갈라 빈 양식과 데이터 파일이 섞이지 않게 한다. */
export const UPLOAD_TEMPLATE_FILE_NAME = '상품속성_업로드양식'

/**
 * 화면 20열 = **BE 응답 18필드 + FE 파생 2열**(화주타입·화주명).
 *
 * ### `variant: 'input'` 이 붙은 열이 편집 가능한 열이다 — 단가 두 열과 바코드만 예외다
 *
 * 단가 두 열은 variant 없이 `withPriceCells` 가, 바코드는 `withBarcodeCell` 이 행별로 셀을 끼운다 (각 열의 주석).
 *
 * | 열 | 편집 | 이유 |
 * |---|---|---|
 * | SKU ID | ✗ | SCM Hub 가 채번한다. 화면이 만들 수도 바꿀 수도 없다 |
 * | 바코드 | **신규 행만** | 채번 키다. 기존 행에서 바꾸면 다른 SKU 이므로 수정이 아니라 신규 등록이다 (LLD §5.6) |
 * | 화주코드 | ✗ | 필터에서 고른 화주로 고정된다. 행마다 다르면 한 요청이 여러 화주를 건드린다 |
 * | 화주타입·화주명 | ✗ | BE 응답에 없는 FE 파생 값이다 |
 * | 감사 4열 | ✗ | 서버가 찍는다 |
 *
 * **물류카테고리 두 열은 의도적으로 없다** — 재부여 배치가 채우는 값이라 띄우면 「여기서 고칠 수
 * 있다」는 오해를 만들고, MWP·MLT 는 prod 보유가 0건이라 띄워도 빈 칸이다 (LLD §6.1).
 *
 * ### 색상·스타일번호·상품단위는 **카테고리 뒤, 브랜드 앞**이다 (LOGISPRD-13480)
 *
 * 뒤에 붙였으니 맨 끝에 두는 편이 쉬운데 그렇게 하지 않는다 — **DB 컬럼 순서(`category` 다음)와
 * 서버 엑셀 열 순서와 같아야** 한다. 다운로드→수정→업로드 운용이 전제라 세 순서가 어긋나면 사람이
 * 잘못 매핑한다 (BE `SkuAttributeExcelDto` KDoc).
 *
 * ### 상품단위는 **빈 칸으로 저장해도 서버가 `PCS` 로 채운다** — 비울 수 없다
 *
 * MA02 파리티다 (`MasterItem.DEFAULT_UNIT_CODE`). 화면은 기본값을 박지 않고 빈 값을 그대로 보낸다 —
 * 상수를 양쪽에 두면 한쪽만 바뀌는 날 갈린다.
 *
 * MA02 FE 도 `_unitCode` 를 서버 값 그대로 왕복시키지만(`productManagement/queries`), **그쪽은
 * `_` 접두 숨김 필드라 사용자가 보지도 편집하지도 못한다.** 그러므로 「빈 칸이 보이는」 문제는
 * MA02 에 없었고 **MA18 이 이 값을 편집 열로 처음 여는 화면이다.** 아래 두 간극이 여기서 새로 생긴다.
 *
 * 1. **저장 직후 셀이 비어 보이고 재조회하면 `PCS` 가 뜬다.** 등록 응답이 `skuId` 만 돌려준다.
 *    그래서 저장 성공 후 화면이 그 행의 값을 `PCS` 로 맞춰 준다 (`SkuAttributeTable.runSave`)
 * 2. **기존 행의 이 셀을 비워 저장하면 「비우기」가 아니라 `PCS` 가 된다.** BE 계약에
 *    「단위를 비워 둔다」가 없기 때문이다. 화면이 저장 전에 그 사실을 알린다
 *
 * ### 허용값 목록을 두지 않는다 — 원장에 그 집합이 없다
 *
 * `unit_code` 는 코드성 값이지만 **셀렉트가 아니라 자유 입력**이다. 유효 코드 집합이 `master_item`
 * 이나 코드 테이블 어디에도 정의돼 있지 않고 BE 검증도 폭(10자)뿐이다. 레포 다른 도메인에
 * `z.enum(['PCS','BOX'])` 선례가 있으나(정산 프로모션의 입고 단위) 그것은 그 도메인의 값 집합이라
 * 여기 그대로 옮기면 **원장에 없는 제약을 발명하는 것**이 된다.
 *
 * > 실제 데이터의 분포를 확인하고 집합을 확정하면 셀렉트로 좁히는 것이 맞다. 별 티켓이다.
 */
export const COLUMNS: PboTableColumn[] = [
  { id: 'c-no', title: 'No', dataIndex: '__rownum', width: 60, align: 'center', fixed: 'left', variant: 'index' },
  { id: 'c-serviceType', title: '화주타입', dataIndex: 'service_type', width: 100 },
  { id: 'c-shipperName', title: '화주명', dataIndex: 'shipper_name', width: 140 },
  { id: 'c-shipperCode', title: '화주코드', dataIndex: 'shipper_code', width: 140 },
  { id: 'c-skuId', title: 'SKU ID', dataIndex: 'sku_id', width: 200 },
  // 바코드는 **variant 를 두지 않는다 — 셀은 `withBarcodeCell` 이 그린다** (LOGISPRD-14571). 신규 행만 입력칸이고
  // 조회된 기존 행은 읽기 전용이다(화주+바코드가 곧 SKU). variant 를 남기면 기존 행 입력칸이 되살아난다
  { id: 'c-barcode', title: '바코드', dataIndex: 'barcode', width: 160, required: true },
  { id: 'c-itemCode', title: '상품코드', dataIndex: 'item_code', width: 130, variant: 'input' },
  { id: 'c-itemName', title: '상품명', dataIndex: 'item_name', width: 260, variant: 'input' },
  { id: 'c-itemOption', title: '상품옵션', dataIndex: 'item_option', width: 120, variant: 'input' },
  { id: 'c-category', title: '카테고리', dataIndex: 'category', width: 110, variant: 'input' },
  { id: 'c-color', title: '색상', dataIndex: 'color', width: 110, variant: 'input' },
  { id: 'c-styleNumber', title: '스타일번호', dataIndex: 'style_number', width: 140, variant: 'input' },
  { id: 'c-unitCode', title: '상품단위', dataIndex: 'unit_code', width: 100, variant: 'input' },
  { id: 'c-brandId', title: '브랜드ID', dataIndex: 'brand_id', width: 110, variant: 'input' },
  { id: 'c-brandName', title: '브랜드명', dataIndex: 'brand_name', width: 140, variant: 'input' },
  // 단가 두 열은 **variant 를 두지 않는다 — 셀은 `PriceCell`(`withPriceCells`)이 그린다** (LOGISPRD-13833).
  // pbo-core `amountInput` 은 값이 null 이면 입력칸 대신 "-" 를 그리고, 소수점을 지우고, 5자리에서 잘라
  // 이 열을 받을 수 없다. variant 를 남겨 두면 `withPriceCells` 없이 이 COLUMNS 를 쓰는 순간 그 셀이
  // 조용히 되살아난다. 편집값은 문자열로 오고 숫자 변환은 `onCellChange` 의 `toPriceOrNull` 이 한다
  { id: 'c-receivablePrice', title: '입고단가', dataIndex: 'receivable_price', width: 110, align: 'right' },
  { id: 'c-payablePrice', title: '출고단가', dataIndex: 'payable_price', width: 110, align: 'right' },
  { id: 'c-createdAt', title: '등록일시', dataIndex: 'created_at', width: 160 },
  { id: 'c-createdBy', title: '등록자', dataIndex: 'created_by', width: 160 },
  { id: 'c-lastModifiedAt', title: '수정일시', dataIndex: 'last_modified_at', width: 160 },
  { id: 'c-lastModifiedBy', title: '수정자', dataIndex: 'last_modified_by', width: 160 },
]

/**
 * 엑셀 업로드가 읽는 헤더 → 행 필드 매핑.
 *
 * **서버 다운로드 엑셀의 헤더와 같아야 한다** (`SkuAttributeExcelDto` 의 `@ExcelField` 이름).
 * 다르면 다운로드→수정→업로드 흐름에서 헤더 매칭이 깨진다.
 *
 * ### 이 매핑이 **위치가 아니라 이름 기반**인 것이 호환성을 만든다
 *
 * LOGISPRD-13480 이 세 열을 가운데(카테고리 뒤)에 끼웠는데, 그래도 **종전 파일의 열이 한 칸씩
 * 밀리지 않는다.** 위치 기반이었다면 색상 값이 브랜드ID 자리로 들어갔고 셋 다 문자열이라 조용했다.
 *
 * ### ⚠️ 다만 서버 다운로드본을 되올릴 때 **네 열은 원래도 읽히지 않는다**
 *
 * 서버 헤더(`SkuAttributeExcelDto` 의 `@ExcelField`)와 이 표의 키가 네 곳에서 다르다. 이 PR 이
 * 만든 것이 아니라 **MA18 원본(LOGISPRD-13286)부터 그랬고**, 아래 열은 `?? null` 로 떨어져 조용히
 * 버려진다.
 *
 * | 서버 헤더 | 이 표의 키 |
 * |---|---|
 * | `품목코드` | `상품코드` |
 * | `품목옵션` | `상품옵션` |
 * | `품목명` | `상품명` |
 * | `브랜드` | `브랜드명` |
 *
 * 이번에 더한 세 키(`색상`·`스타일번호`·`상품단위`)는 서버 헤더와 **정확히 일치한다.** 위 네 개를
 * 맞추는 것은 기존 동작을 바꾸는 일이라 **별 티켓**으로 둔다 — 여기서 같이 고치면 이 PR 의
 * AS-IS/TO-BE 밖의 변경이 섞인다.
 */
export const UPLOAD_HEADER_MAP: Record<string, string> = {
  바코드: 'barcode',
  상품코드: 'item_code',
  상품명: 'item_name',
  상품옵션: 'item_option',
  카테고리: 'category',
  색상: 'color',
  스타일번호: 'style_number',
  상품단위: 'unit_code',
  브랜드ID: 'brand_id',
  브랜드명: 'brand_name',
  입고단가: 'receivable_price',
  출고단가: 'payable_price',
}

/**
 * 컬럼 폭 — **BE 검증과 같은 값이다** (`RegisterSkuAttributeService` 의 `MAX_*_LENGTH`).
 *
 * 화면에서 먼저 막는 것은 **채번 낭비를 줄이기 위해서다.** BE 도 같은 검사를 하지만 그쪽은
 * 요청이 도착한 뒤이고, 여기서 막으면 사용자가 업로드 전에 고친다. 둘 중 하나만 두지 않는 이유는
 * 화면 검증은 우회할 수 있고 BE 검증은 사용자에게 늦게 닿기 때문이다.
 */
export const MAX_LENGTHS: Partial<Record<keyof SkuAttributeRow, number>> = {
  barcode: 60,
  item_code: 100,
  item_option: 100,
  item_name: 120,
  category: 50,
  color: 50,
  style_number: 80,
  unit_code: 10,
  brand_id: 50,
  brand_name: 50,
}

/** 숫자로 다루는 열 — 셀 편집 결과를 경계에서 변환한다 ([toPriceOrNull]). */
export const NUMERIC_COLUMNS = new Set(['receivable_price', 'payable_price'])

/** `DECIMAL(10,2)` — 정수부 8자리, 소수 2자리. */
export const PRICE_MAX = 99999999.99
export const PRICE_SCALE = 2

/** 라우트 메뉴 코드 (`routesWithElements` 의 `ma18`). 탭 닫기 정리가 이 문자열로 목록 캐시를 찾는다 (LOGISPRD-14923) */
export const MENU_CODE = 'ma18'

/**
 * 한 페이지 행 수 — **화면 1페이지 = 서버 1페이지**다 (LOGISPRD-14923).
 *
 * 2,000 인 것은 BE 가 그보다 크게 주지 않기 때문이다. 목록 API 는 Spring Data `Pageable` 을 받고, 레포에
 * `max-page-size` 오버라이드가 없어 기본 상한 2,000 으로 `size` 를 줄인다. 초판은 10,000 을 요청하고
 * 「받은 건수 < 요청 크기 = 마지막 페이지」로 순회를 끝내서, 첫 응답 2,000건을 끝으로 오판해 나머지를
 * 경고 없이 버렸다(GLOWNY 7,229건 중 2,000건만 노출).
 *
 * 이 값을 2,000 위로 올리면 같은 일이 다시 난다 — 서버는 2,000건과 `totalPages` 를 그 크기 기준으로 주고,
 * 화면 페이저는 이 값으로 페이지를 나눠 둘이 어긋난다. 올리려면 BE 상한부터 바꾼다.
 */
export const PAGE_SIZE = 2000

/**
 * 조회 결과 캐시 보관 시간. 다른 메뉴 탭에 갔다가 돌아왔을 때 **재조회하지 않고** 보던 페이지를 그대로 보여 주는
 * 시간이다 — 화면이 사라지면 캐시는 쓰는 곳이 없는 상태가 되고, react-query 기본(5분)이 지나면 버려진다.
 */
export const LIST_CACHE_TIME_MS = 30 * 60 * 1000
